import { ControlCodes } from '@bcube/shared/constants';
import { createBrowserRouter, Navigate } from 'react-router';

import { PermissionRoute } from '@/components/auth/permission-route';
import { ProtectedRoute } from '@/components/auth/protected-route';
// Module landing page component for placeholder modules
import { ModuleLandingPage } from '@/components/module/module-landing-page';
import { commercialModule, connectivityModule, productionModule } from '@/config/modules';
import { GuestLayout } from '@/layouts/guest-layout';
import { MainLayout } from '@/layouts/main-layout';
// Admin pages
import { AdminLandingPage } from '@/pages/admin';
import { ControlsPage } from '@/pages/admin/controls';
import { ProfileListPage } from '@/pages/admin/profiles';
import { UserProfilePage } from '@/pages/admin/user';
import { UserCreatePage, UserEditPage, UserListPage } from '@/pages/admin/users';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password';
// Auth pages
import { LoginPage } from '@/pages/auth/login';
import { ResetPasswordPage } from '@/pages/auth/reset-password';
import { SetPasswordPage } from '@/pages/auth/set-password';
// Commercial pages
import { AccountCreatePage } from '@/pages/commercial/accounts/create';
import { AccountDetailsPage } from '@/pages/commercial/accounts/details';
import { AccountListPage } from '@/pages/commercial/accounts/list';
import { ContractListPage } from '@/pages/commercial/contracts/list';
// External pages
import { ExternalDocumentUploadPage } from '@/pages/commercial/credit-analysis/external-form';
import { CreditAnalysisListPage } from '@/pages/commercial/credit-analysis/list';
import { CreditAnalysisUpdatePage } from '@/pages/commercial/credit-analysis/update';
import {
  PriceTableClonePage,
  PriceTableCreatePage,
  PriceTableEditPage,
  PriceTableListPage,
} from '@/pages/commercial/price-table';
import { ProductListPage } from '@/pages/commercial/products';
import { ProposalAcceptancePage } from '@/pages/commercial/proposals/acceptance';
import { ProposalCreatePage } from '@/pages/commercial/proposals/create';
import { ProposalListPage } from '@/pages/commercial/proposals/list';
import { ProposalUpdatePage } from '@/pages/commercial/proposals/update';
import { SalespersonListPage } from '@/pages/commercial/salespersons/list';
// Configurador SSO (embedded MFE)
import { ConfiguradorSsoPage } from '@/pages/configurador-sso';
// Connectivity pages
import {
  BatchLoraActivationPage,
  KoraSyncPage,
  LoraActivationLogsPage,
  LoraActivationPage,
} from '@/pages/connectivity';
// Main pages
import { ErrorPage } from '@/pages/error';
import { FinancialReceiptsListPage } from '@/pages/financial/receipts/list';
import { HomePage } from '@/pages/home';
import { NotFoundPage } from '@/pages/not-found';
// Production pages
import {
  ConfigProfileCreatePage,
  ConfigProfileEditPage,
  ConfigProfileListPage,
  ConfigProfileUpdateFromOrderPage,
} from '@/pages/production/config-profiles';
import { DeviceCreatePage, DeviceEditPage, DeviceListPage } from '@/pages/production/devices';
import { ProductionLogsPage } from '@/pages/production/logs';
import { AutoTestLogDetailPage } from '@/pages/production/logs/auto-test/detail';
import { AutoTestLogListPage } from '@/pages/production/logs/auto-test/list';
import { ConfigurationLogDetailPage } from '@/pages/production/logs/configurator/detail';
import { ConfigurationLogListPage } from '@/pages/production/logs/configurator/list';
import { FirmwareUpdateLogDetailPage } from '@/pages/production/logs/firmware-update/detail';
import { FirmwareUpdateLogListPage } from '@/pages/production/logs/firmware-update/list';
import { IdentificationLogDetailPage } from '@/pages/production/logs/identificator/detail';
import { IdentificationLogListPage } from '@/pages/production/logs/identificator/list';
import {
  AutoTestPage,
  CheckConfigurationPage,
  CommandsPage,
  ConfiguratorPage,
  FirmwareUpdatePage,
  IdentificatorPage,
  ProductionToolsPage,
} from '@/pages/production/tools';

// Placeholder pages for modules (to be implemented)
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-2 text-muted-foreground">Em desenvolvimento...</p>
    </div>
  );
}

export const router = createBrowserRouter([
  // Redirect root to home
  {
    path: '/',
    lazy: async () => {
      const { Navigate } = await import('react-router');
      return { Component: () => <Navigate to="/home" replace /> };
    },
  },

  // Guest routes (login, forgot password, etc.)
  {
    element: <GuestLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
    ],
  },

  // Public routes (no layout, external access)
  {
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/external-link/:linkId/documents',
        element: <ExternalDocumentUploadPage />,
      },
      {
        path: '/proposals/acceptance/:token',
        element: <ProposalAcceptancePage />,
      },
    ],
  },

  // Protected routes - set-password (fullscreen, no sidebar)
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/set-password',
        element: <SetPasswordPage />,
      },
    ],
  },

  // Protected routes - all use the same MainLayout with unified sidebar
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <ErrorPage />,
        children: [
          // Home
          {
            path: '/home',
            element: <HomePage />,
          },

          // Configurador SSO (MFE embed) — o splat carrega a rota interna do MFE
          // (ex: /configurador-sso/auto-test embute o configurador em /auto-test)
          {
            path: '/configurador-sso/*',
            element: <ConfiguradorSsoPage />,
          },

          // Special: own user profile — always accessible to authenticated users
          // (kept out of the Admin module guard so non-admin users can edit their avatar)
          {
            path: '/admin/user/profile',
            element: <UserProfilePage />,
          },

          // ============================================================
          // Admin module — requires `admin` permission prefix
          // ============================================================
          {
            element: <PermissionRoute require={ControlCodes.admin} />,
            children: [
              // Admin landing (module-level guard only)
              {
                path: '/admin',
                element: <AdminLandingPage />,
              },

              // Users sub-module (prefix)
              {
                element: <PermissionRoute require={ControlCodes.admin_user} />,
                children: [
                  {
                    path: '/admin/users',
                    element: <UserListPage />,
                  },
                  {
                    path: '/admin/users/:id',
                    element: <PlaceholderPage title="Detalhes do Usuário" />,
                  },
                ],
              },

              // Create user (exact)
              {
                element: <PermissionRoute require={ControlCodes.admin_user_create} match="exact" />,
                children: [
                  {
                    path: '/admin/users/create',
                    element: <UserCreatePage />,
                  },
                ],
              },

              // Edit user (exact)
              {
                element: <PermissionRoute require={ControlCodes.admin_user_update} match="exact" />,
                children: [
                  {
                    path: '/admin/users/:id/edit',
                    element: <UserEditPage />,
                  },
                ],
              },

              // Profiles sub-module (prefix)
              {
                element: <PermissionRoute require={ControlCodes.admin_profile} />,
                children: [
                  {
                    path: '/admin/profiles',
                    element: <ProfileListPage />,
                  },
                  {
                    path: '/admin/profiles/:id',
                    element: <PlaceholderPage title="Editar Permissões" />,
                  },
                ],
              },

              // Control sub-module (prefix)
              {
                element: <PermissionRoute require={ControlCodes.admin_control} />,
                children: [
                  {
                    path: '/admin/control',
                    element: <ControlsPage />,
                  },
                ],
              },
            ],
          },

          // ============================================================
          // Commercial module — requires `commercial` permission prefix
          // ============================================================
          {
            element: <PermissionRoute require={ControlCodes.commercial} />,
            children: [
              // Commercial landing (module-level guard only)
              {
                path: '/commercial',
                element: (
                  <ModuleLandingPage
                    title={commercialModule.title}
                    description={commercialModule.description}
                    actions={commercialModule.landingActions}
                  />
                ),
              },

              // Products sub-module (prefix)
              {
                element: <PermissionRoute require={ControlCodes.commercial_product} />,
                children: [
                  {
                    path: '/commercial/products',
                    element: <ProductListPage />,
                  },
                  // Next.js compatible route
                  {
                    path: '/commercial/product/management',
                    element: <ProductListPage />,
                  },
                  {
                    path: '/commercial/product/category',
                    lazy: async () => {
                      const { Navigate } = await import('react-router');
                      return {
                        Component: () => (
                          <Navigate to="/commercial/products?tab=categories" replace />
                        ),
                      };
                    },
                  },
                ],
              },

              // Accounts sub-module — list (prefix)
              {
                element: <PermissionRoute require={ControlCodes.commercial_accounts} />,
                children: [
                  {
                    path: '/commercial/accounts',
                    element: <AccountListPage />,
                  },
                ],
              },

              // Account create (exact)
              {
                element: (
                  <PermissionRoute require={ControlCodes.commercial_accounts_new} match="exact" />
                ),
                children: [
                  {
                    path: '/commercial/accounts/create',
                    element: <AccountCreatePage />,
                  },
                ],
              },

              // Account detail (prefix — navegação: libera qualquer `access:tab:*` filha)
              {
                element: <PermissionRoute require={ControlCodes.commercial_accounts_access} />,
                children: [
                  {
                    path: '/commercial/accounts/:id',
                    element: <AccountDetailsPage />,
                  },
                ],
              },

              // Proposal sub-module (prefix)
              {
                element: <PermissionRoute require={ControlCodes.commercial_salesperson_view} />,
                children: [{ path: '/commercial/salespersons', element: <SalespersonListPage /> }],
              },
              {
                element: <PermissionRoute require={ControlCodes.commercial_proposal} />,
                children: [
                  {
                    path: '/commercial/proposal',
                    element: <ProposalListPage />,
                  },
                  // Vite route alias (plural to singular redirect)
                  {
                    path: '/commercial/proposals',
                    lazy: async () => {
                      const { Navigate } = await import('react-router');
                      return {
                        Component: () => <Navigate to="/commercial/proposal" replace />,
                      };
                    },
                  },
                ],
              },

              // Proposal create (Categoria B — pending `commercial:proposal:create`)
              {
                path: '/commercial/proposal/form/create',
                element: <ProposalCreatePage />,
              },

              // Proposal update (Categoria B — pending `commercial:proposal:update`)
              {
                path: '/commercial/proposal/form/update/:id',
                element: <ProposalUpdatePage />,
              },

              // Price-table sub-module — list (prefix)
              {
                element: <PermissionRoute require={ControlCodes.commercial_price_table} />,
                children: [
                  {
                    path: '/commercial/price-tables',
                    element: <PriceTableListPage />,
                  },
                  // Next.js compatible route
                  {
                    path: '/commercial/price-table',
                    element: <PriceTableListPage />,
                  },
                ],
              },

              // Price-table create + clone (exact)
              {
                element: (
                  <PermissionRoute
                    require={ControlCodes.commercial_price_table_create}
                    match="exact"
                  />
                ),
                children: [
                  {
                    path: '/commercial/price-table/form/create',
                    element: <PriceTableCreatePage />,
                  },
                  {
                    path: '/commercial/price-table/form/clone/:id',
                    element: <PriceTableClonePage />,
                  },
                ],
              },

              // Price-table edit (exact)
              {
                element: (
                  <PermissionRoute
                    require={ControlCodes.commercial_price_table_edit}
                    match="exact"
                  />
                ),
                children: [
                  {
                    path: '/commercial/price-table/form/edit/:id',
                    element: <PriceTableEditPage />,
                  },
                ],
              },

              // Credit-analysis sub-module — list (prefix)
              {
                element: <PermissionRoute require={ControlCodes.commercial_credit_analysis} />,
                children: [
                  {
                    path: '/commercial/credit-analysis',
                    element: <CreditAnalysisListPage />,
                  },
                ],
              },

              // Credit-analysis update (Categoria B — pending `commercial:credit-analysis:update`)
              {
                path: '/commercial/credit-analysis/:id',
                element: <CreditAnalysisUpdatePage />,
              },
            ],
          },
          {
            path: '/commercial/contracts',
            element: <ContractListPage />,
          },
          {
            element: <PermissionRoute require={ControlCodes.financial_payment_confirmation_view} />,
            children: [
              { path: '/financial', element: <Navigate to="/financial/receipts" replace /> },
              { path: '/financial/receipts', element: <FinancialReceiptsListPage /> },
            ],
          },

          // ============================================================
          // Production module — requires `production` permission prefix
          // ============================================================
          {
            element: <PermissionRoute require={ControlCodes.production} />,
            children: [
              // Production landing (module-level guard only)
              {
                path: '/production',
                element: (
                  <ModuleLandingPage
                    title={productionModule.title}
                    description={productionModule.description}
                    actions={productionModule.landingActions}
                  />
                ),
              },

              // Devices sub-module — list (prefix)
              {
                element: <PermissionRoute require={ControlCodes.production_device} />,
                children: [
                  {
                    path: '/production/devices',
                    element: <DeviceListPage />,
                  },
                ],
              },

              // Device create (Categoria B — pending `production:device:create`)
              {
                path: '/production/devices/create',
                element: <DeviceCreatePage />,
              },

              // Device edit (Categoria B — pending `production:device:edit`)
              {
                path: '/production/devices/edit',
                element: <DeviceEditPage />,
              },

              // Config-profiles sub-module — list + aliases (prefix)
              {
                element: (
                  <PermissionRoute require={ControlCodes.production_configuration_profile} />
                ),
                children: [
                  {
                    path: '/production/config-profiles',
                    element: <ConfigProfileListPage />,
                  },
                  // Next.js compatible route
                  {
                    path: '/production/configuration-profile/management',
                    element: <ConfigProfileListPage />,
                  },
                ],
              },

              // Config-profile create/edit/update-from-order (Categoria B — pending codes)
              {
                path: '/production/config-profiles/create',
                element: <ConfigProfileCreatePage />,
              },
              {
                path: '/production/config-profiles/edit',
                element: <ConfigProfileEditPage />,
              },
              {
                path: '/production/config-profiles/update-from-order',
                element: <ConfigProfileUpdateFromOrderPage />,
              },
              {
                path: '/production/configuration-profile/form/create',
                element: <ConfigProfileCreatePage />,
              },
              {
                path: '/production/configuration-profile/form/update',
                element: <ConfigProfileEditPage />,
              },
              {
                path: '/production/configuration-profile/form/update-from-production-order',
                element: <ConfigProfileUpdateFromOrderPage />,
              },

              // Tools sub-module — landing (prefix)
              {
                element: <PermissionRoute require={ControlCodes.production_tool} />,
                children: [
                  {
                    path: '/production/tools',
                    element: <ProductionToolsPage />,
                  },
                ],
              },

              // Tool: auto-test (exact)
              {
                element: (
                  <PermissionRoute require={ControlCodes.production_tool_auto_test} match="exact" />
                ),
                children: [
                  {
                    path: '/production/tools/auto-test',
                    element: <AutoTestPage />,
                  },
                ],
              },

              // Tool: configurator (exact)
              {
                element: (
                  <PermissionRoute
                    require={ControlCodes.production_tool_configurator}
                    match="exact"
                  />
                ),
                children: [
                  {
                    path: '/production/tools/configurator',
                    element: <ConfiguratorPage />,
                  },
                ],
              },

              // Tool: identifier (exact — code is `identifier`, route is `identificator`)
              {
                element: (
                  <PermissionRoute
                    require={ControlCodes.production_tool_identifier}
                    match="exact"
                  />
                ),
                children: [
                  {
                    path: '/production/tools/identificator',
                    element: <IdentificatorPage />,
                  },
                ],
              },

              // Tool: firmware-update (Categoria B — pending `production:tool:firmware-update`)
              {
                path: '/production/tools/firmware-update',
                element: <FirmwareUpdatePage />,
              },

              // Tool: check-configuration (Categoria B — pending specific code)
              {
                path: '/production/tools/check-configuration',
                element: <CheckConfigurationPage />,
              },

              // Tool: commands (Categoria B — pending specific code)
              {
                path: '/production/tools/commands',
                element: <CommandsPage />,
              },

              // Logs sub-module — landing (prefix)
              {
                element: <PermissionRoute require={ControlCodes.production_logs} />,
                children: [
                  {
                    path: '/production/logs',
                    element: <ProductionLogsPage />,
                  },
                ],
              },

              // Logs: auto-test (exact) — list + detail
              {
                element: (
                  <PermissionRoute require={ControlCodes.production_logs_auto_test} match="exact" />
                ),
                children: [
                  {
                    path: '/production/logs/auto-test',
                    element: <AutoTestLogListPage />,
                  },
                  {
                    path: '/production/logs/auto-test/:id',
                    element: <AutoTestLogDetailPage />,
                  },
                ],
              },

              // Logs: configurator (exact) — list + detail
              {
                element: (
                  <PermissionRoute
                    require={ControlCodes.production_logs_configurator}
                    match="exact"
                  />
                ),
                children: [
                  {
                    path: '/production/logs/configurator',
                    element: <ConfigurationLogListPage />,
                  },
                  {
                    path: '/production/logs/configurator/:id',
                    element: <ConfigurationLogDetailPage />,
                  },
                ],
              },

              // Logs: identifier (exact) — list + detail
              {
                element: (
                  <PermissionRoute
                    require={ControlCodes.production_logs_identifier}
                    match="exact"
                  />
                ),
                children: [
                  {
                    path: '/production/logs/identificator',
                    element: <IdentificationLogListPage />,
                  },
                  {
                    path: '/production/logs/identificator/:id',
                    element: <IdentificationLogDetailPage />,
                  },
                ],
              },

              // Logs: firmware-update (Categoria B — pending `production:logs:firmware-update`)
              {
                path: '/production/logs/firmware-update',
                element: <FirmwareUpdateLogListPage />,
              },
              {
                path: '/production/logs/firmware-update/:id',
                element: <FirmwareUpdateLogDetailPage />,
              },
            ],
          },

          // ============================================================
          // Connectivity module — requires `connectivity` permission prefix
          // ============================================================
          {
            element: <PermissionRoute require={ControlCodes.connectivity} />,
            children: [
              // Connectivity landing (module-level guard only)
              {
                path: '/connectivity',
                element: (
                  <ModuleLandingPage
                    title={connectivityModule.title}
                    description={connectivityModule.description}
                    actions={connectivityModule.landingActions}
                  />
                ),
              },

              // Kora-sync (exact — single page, no hierarchical children)
              {
                element: (
                  <PermissionRoute require={ControlCodes.connectivity_kora_sync} match="exact" />
                ),
                children: [
                  {
                    path: '/connectivity/kora-sync',
                    element: <KoraSyncPage />,
                  },
                ],
              },

              // LoRa activation (prefix — main page)
              {
                element: <PermissionRoute require={ControlCodes.connectivity_lora_activation} />,
                children: [
                  {
                    path: '/connectivity/lora-activation',
                    element: <LoraActivationPage />,
                  },
                ],
              },

              // Batch activation (Categoria B — pending `connectivity:lora-activation:batch`)
              {
                path: '/connectivity/batch-lora-activation',
                element: <BatchLoraActivationPage />,
              },

              // LoRa activation logs (Categoria B — pending `connectivity:lora-activation:logs`)
              {
                path: '/connectivity/lora-activation-logs',
                element: <LoraActivationLogsPage />,
              },
            ],
          },
        ],
      },
    ],
  },

  // 404 page
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
